import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {Paper, Typography, Tooltip, IconButton, Fab, Dialog} from '@material-ui/core';
import {Add, CloudDownload, Refresh} from '@material-ui/icons';
import { BackButton, getDimensionsOfPagination, hasAtLeastOnePermission } from '../../sc-utilities';
import { Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { hasPermission, localization } from '../../sc-utilities';
import BillingAccountDelete from './BillingAccountDelete';
import * as actions from './store/billingAccountActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import moment from 'moment';
import {snackActions} from "../../sc-utilities/SnackBar";
import {download} from "../../sc-utilities/download.utils";
import config from "../../../config/config";
import AppLogoLoading from "../../sc-logo";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    loadingScreen: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const BillingAccountList = props => {
    const { classes, translations, permissions, onFetchBillingAccounts, onFetchBillingAccountsCount, fetching, saveColumnsFilter, activeCustomer, onBulkDeleteBillingAccounts, tableColumns, redirectHandler, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, billingAccountIDs: [] });

    const [downloading, setDownloading] = useState(false);

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['billing_account_delete']),
        selectionProps: rowData => {
            let disabled = false;
            if (rowData.Deleted) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 230 < 300 ? 300 : window.innerHeight - 230,
        maxBodyHeight: window.innerHeight - 230,
        pageSizeOptions: [20, 50, 100],
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        q['ActivationDate'] = q['ActivationDate'] ? moment(q['ActivationDate']).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss') : null;
        q['ExpiresAt'] = q['ExpiresAt'] ? moment(q['ExpiresAt']).set({ hour: 0, minute: 0, second: 0 }).format('YYYY-MM-DD HH:mm:ss') : null;
        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await onFetchBillingAccountsCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let size = getDimensionsOfPagination(count);
            query['pageSize'] = size.pageSize;
            rowsPageSizeInit = size.rowsPageSize;
            pageSize = size.pageSize;
        }
        let result = count ? await onFetchBillingAccounts(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['billing_accounts'] || 'Billing accounts'}</Typography>
                {hasPermission(permissions, 'billing_account_create') ? <Fab
                    id="create_billing_account"
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/billing/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {
                hasPermission(permissions, 'account_export_license_usage') &&
                <Tooltip title={translations[`export_license_usage`] || "Export license usage"}>
                    <IconButton
                        id={`export_license_usage`}
                        component={'div'}
                        aria-label={"export_license_usage"}
                        onClick={() => handleDownload()}
                    >
                        <CloudDownload />
                    </IconButton>
                </Tooltip>
            }
        </div>
    }

    const handleDownload = async () => {
        setDownloading(true);
        const objectURL = `${config.backend_domain}:${config.backend_port}/account/export-license-usage?ActiveCustomerID=${activeCustomer}`;
        const exportDataSuccessful = await download(objectURL);
        tableRef.current && tableRef.current.onQueryChange();
        if (!exportDataSuccessful) {
            return snackActions.snackBarComponent(translations['download_error_msg'] || `Could not download file `, 'error');
        }
        setDownloading(false);
    };

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, billingAccountIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteBillingAccounts({ AccountIDs: deleteModalState.billingAccountIDs, ActiveCustomerID: activeCustomer });
        setDeleteModalState({ open: false, billingAccountIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = billingAccountIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, billingAccountIDs: billingAccountIDs });
    };

    // Table Settings
    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["billing_accounts"] || "Billing accounts"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(billingAccount => billingAccount.AccountID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`BillingAccount`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'billing_account_delete') ? (
                <BillingAccountDelete
                    entityName={translations['billing_accounts'] || 'Billing accounts'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}

            <Dialog disableBackdropClick={true} disableEscapeKeyDown={false} open={downloading}
                    PaperProps={{
                        classes: {
                            root: classes.loadingScreen
                        }
                    }}
            >
                <AppLogoLoading />
            </Dialog>
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.billing.accounts.fetching
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchBillingAccountsCount: filter => dispatch(actions.fetchBillingAccountCount(filter)),
        onFetchBillingAccounts: filter => dispatch(actions.fetchBillingAccount(filter)),
        onBulkDeleteBillingAccounts: data => dispatch(actions.bulkDeleteBillingAccounts(data)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(BillingAccountList));